import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Copy, ExternalLink } from 'lucide-react';
import { copyToClipboard } from '../../utils/helpers';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetLink('');
    try {
      const { data } = await authAPI.forgotPassword(email);
      toast.success(data.message);
      if (data.reset_link) {
        setResetLink(data.reset_link);
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    await copyToClipboard(resetLink);
    toast.success('Reset link copied!');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="text-3xl font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="forgot-heading">
            Forgot Password?
          </h2>
          <p className="mt-2 text-muted-foreground">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-form">
          <div className="space-y-2">
            <Label>Email Address</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              data-testid="forgot-email-input"
              className="h-11"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full h-11" data-testid="forgot-submit-button">
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        {resetLink && (
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 space-y-3" data-testid="reset-link-display">
            <div className="flex items-start gap-2">
              <div className="text-sm font-medium text-blue-800 dark:text-blue-300">
                Reset link generated (would normally be emailed)
              </div>
            </div>
            <div className="p-3 rounded bg-white dark:bg-zinc-900 text-xs font-mono break-all border">
              {resetLink}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copyLink} className="flex-1">
                <Copy className="h-3 w-3 mr-1" /> Copy
              </Button>
              <Link to={resetLink.split(window.location.origin)[1] || '/reset-password'} className="flex-1">
                <Button size="sm" className="w-full">
                  <ExternalLink className="h-3 w-3 mr-1" /> Open
                </Button>
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">Link expires in 1 hour</p>
          </div>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Remember your password?{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};
